import { useCallback, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import styled from "styled-components";
import { FEATURES } from "@src/constants";
import { theme } from "@src/theme";
import { IPlaylistTag } from "@src/types/playlistTag";

/**
 * `AnimatePresence` is typed as returning `Element | undefined`, which the React
 * types this project is on reject as a JSX component. Same cast the router
 * already makes for it — see `AnimatedRoutes`.
 */
const SafeAnimatePresence = AnimatePresence as React.ComponentType<
  React.PropsWithChildren<{ initial?: boolean }>
>;

export type PlaylistTagSelection = {
  selectedCategories: string[];
  /**
   * Subtag ids keyed by the tag they hang off, so unpicking a tag drops the
   * subtags picked under it [ROVR-3982].
   */
  selectedSubtags: Record<string, string[]>;
  /** The picked tags that actually have subtags to offer, in display order. */
  categoriesWithSubtags: IPlaylistTag[];
  /** The one subtag section that is open, if any. */
  expandedCategoryId: string | null;
  /**
   * What a save sends. Read back off the tag list rather than straight off the
   * selection, so an id can only travel while the tag it hangs off is still
   * picked and still offers it — nothing survives a tag being unpicked or the
   * tag list changing under the popup, and a subtag offered by two picked tags
   * is sent once [ROVR-3982].
   */
  selectedSubtagIds: string[];
  onSelectCategory: (categoryId: string) => void;
  onSelectSubtag: (categoryId: string, subtagId: string) => void;
  onExpandCategory: (categoryId: string) => void;
  /**
   * Start over from the given tags and subtags — nothing picked by default. A
   * subtag only lands under a tag of `categories` that offers it, so call this
   * once the tag list has loaded.
   */
  reset: (categoryIds?: string[], subtagIds?: string[]) => void;
};

/**
 * The tag + subtag picking state shared by the submit popup and the admin's
 * "Update playlist tags" popup [ROVR-3982].
 */
export const usePlaylistTagSelection = (
  categories: IPlaylistTag[] | undefined
): PlaylistTagSelection => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubtags, setSelectedSubtags] = useState<
    Record<string, string[]>
  >({});
  /**
   * The section opened by hand. Cleared on a fresh pick, so a newly picked tag
   * always brings its own subtags into view.
   */
  const [manuallyExpandedCategory, setManuallyExpandedCategory] = useState<
    string | null
  >(null);

  const reset = useCallback(
    (categoryIds: string[] = [], subtagIds: string[] = []): void => {
      setSelectedCategories(categoryIds);
      const grouped: Record<string, string[]> = {};
      categories?.forEach((category) => {
        if (!categoryIds.includes(category.documentId)) return;
        const picked = (category.subPlaylistTags ?? [])
          .map((subtag) => subtag.documentId)
          .filter((subtagId) => subtagIds.includes(subtagId));
        if (picked.length) grouped[category.documentId] = picked;
      });
      setSelectedSubtags(grouped);
      setManuallyExpandedCategory(null);
    },
    [categories]
  );

  const onSelectCategory = (categoryId: string): void => {
    const wasPicked = selectedCategories.includes(categoryId);
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
    setSelectedSubtags((prev) => {
      if (!prev[categoryId]) return prev;
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
    // A new pick opens its own section. Dropping a tag leaves whatever section
    // was opened by hand alone — unless it was that tag's, which stops existing.
    if (!wasPicked) setManuallyExpandedCategory(null);
  };

  const onSelectSubtag = (categoryId: string, subtagId: string): void => {
    setSelectedSubtags((prev) => {
      const current = prev[categoryId] || [];
      return {
        ...prev,
        [categoryId]: current.includes(subtagId)
          ? current.filter((id) => id !== subtagId)
          : [...current, subtagId],
      };
    });
  };

  const categoriesWithSubtags = useMemo<IPlaylistTag[]>(() => {
    if (!FEATURES.PLAYLIST_SUBTAGS_ENABLED || !categories) return [];
    return categories.filter(
      (category) =>
        selectedCategories.includes(category.documentId) &&
        !!category.subPlaylistTags?.length
    );
  }, [categories, selectedCategories]);

  /**
   * Only one subtag section is open at a time — otherwise the modal grows past
   * the viewport once a third tag is picked. It is the last tag picked, unless
   * another section's header was opened by hand.
   */
  const expandedCategoryId = useMemo<string | null>(() => {
    const ids = categoriesWithSubtags.map((category) => category.documentId);
    if (!ids.length) return null;
    if (manuallyExpandedCategory && ids.includes(manuallyExpandedCategory)) {
      return manuallyExpandedCategory;
    }
    return (
      [...selectedCategories].reverse().find((id) => ids.includes(id)) ?? null
    );
  }, [categoriesWithSubtags, manuallyExpandedCategory, selectedCategories]);

  const selectedSubtagIds = useMemo<string[]>(() => {
    const ids = categoriesWithSubtags.flatMap((category) => {
      const picked = selectedSubtags[category.documentId];
      if (!picked?.length) return [];
      return (category.subPlaylistTags ?? [])
        .map((subtag) => subtag.documentId)
        .filter((subtagId) => picked.includes(subtagId));
    });
    return Array.from(new Set(ids));
  }, [categoriesWithSubtags, selectedSubtags]);

  return {
    selectedCategories,
    selectedSubtags,
    categoriesWithSubtags,
    expandedCategoryId,
    selectedSubtagIds,
    onSelectCategory,
    onSelectSubtag,
    onExpandCategory: setManuallyExpandedCategory,
    reset,
  };
};

type PlaylistSubtagsPickerProps = Pick<
  PlaylistTagSelection,
  | "categoriesWithSubtags"
  | "expandedCategoryId"
  | "selectedSubtags"
  | "onSelectSubtag"
  | "onExpandCategory"
>;

/**
 * One section per picked tag that has subtags, of which only one is open at a
 * time [ROVR-3982].
 */
const PlaylistSubtagsPicker: React.FC<PlaylistSubtagsPickerProps> = ({
  categoriesWithSubtags,
  expandedCategoryId,
  selectedSubtags,
  onSelectSubtag,
  onExpandCategory,
}) => {
  const shouldReduceMotion = useReducedMotion();
  /**
   * Sections and their pill lists grow and shrink rather than popping in and
   * out. The spacing between them is animated as a margin instead of being left
   * to the flex `gap`, which cannot tween — a section on its way out would hold
   * its gap open until the last frame and then snap [ROVR-3982].
   */
  const reveal = useMemo(() => {
    const duration = shouldReduceMotion ? 0 : 0.22;
    return {
      transition: { duration, ease: "easeInOut" as const },
      collapsed: { height: 0, opacity: 0 },
    };
  }, [shouldReduceMotion]);

  return (
    /*
     * Always mounted, so `AnimatePresence` outlives the last section and can
     * still play it out. It collapses to nothing while empty.
     */
    <SubtagsContainer>
      <SafeAnimatePresence>
        {categoriesWithSubtags.map((category) => {
          const isExpanded = category.documentId === expandedCategoryId;
          const title = `Select your ${category.label} subtags`;
          const picked = selectedSubtags[category.documentId] ?? [];
          /*
           * Collapsed, the pills are gone, so the header says what is picked
           * under it. Without this only part of what a save was about to send
           * would ever be on screen [ROVR-3982].
           */
          const pickedLabels = picked.length
            ? (category.subPlaylistTags ?? [])
                .filter((subtag) => picked.includes(subtag.documentId))
                .map((subtag) => subtag.label)
                .join(", ")
            : "";

          return (
            <Reveal
              key={category.documentId}
              initial={{ ...reveal.collapsed, marginBottom: 0 }}
              animate={{ height: "auto", opacity: 1, marginBottom: 8 }}
              exit={{ ...reveal.collapsed, marginBottom: 0 }}
              transition={reveal.transition}
            >
              <SubtagsSection>
                {isExpanded ? (
                  <SubtagsTitle>{title}</SubtagsTitle>
                ) : (
                  <SubtagsToggle
                    type="button"
                    aria-expanded={false}
                    onClick={(): void => onExpandCategory(category.documentId)}
                  >
                    <SubtagsToggleLabel>
                      <SubtagsTitle>{title}</SubtagsTitle>
                      {!!pickedLabels && (
                        <SummaryReveal
                          initial={{ height: 0, opacity: 0, marginTop: 0 }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                            marginTop: 2,
                          }}
                          transition={reveal.transition}
                        >
                          <SubtagsSummary>{pickedLabels}</SubtagsSummary>
                        </SummaryReveal>
                      )}
                    </SubtagsToggleLabel>
                    <ChevronDown size={14} aria-hidden />
                  </SubtagsToggle>
                )}

                {/*
                 * `initial={false}` so a section that mounts already expanded is
                 * not animated twice over — its own entrance grows this list into
                 * view. Only later toggles play.
                 */}
                <SafeAnimatePresence initial={false}>
                  {isExpanded && (
                    <Reveal
                      key="subtags-list"
                      initial={{ ...reveal.collapsed, marginTop: 0 }}
                      animate={{ height: "auto", opacity: 1, marginTop: 8 }}
                      exit={{ ...reveal.collapsed, marginTop: 0 }}
                      transition={reveal.transition}
                    >
                      <SubtagsList>
                        {category.subPlaylistTags?.map((subtag) => {
                          const isActive = picked.includes(subtag.documentId);
                          return (
                            <SubtagPill
                              key={subtag.documentId}
                              type="button"
                              aria-pressed={isActive}
                              $active={isActive}
                              onClick={(): void =>
                                onSelectSubtag(
                                  category.documentId,
                                  subtag.documentId
                                )
                              }
                            >
                              {subtag.label}
                            </SubtagPill>
                          );
                        })}
                      </SubtagsList>
                    </Reveal>
                  )}
                </SafeAnimatePresence>
              </SubtagsSection>
            </Reveal>
          );
        })}
      </SafeAnimatePresence>
    </SubtagsContainer>
  );
};

/**
 * The 12px that used to close this box off is now 8px of animated margin under
 * the last section plus the 4px here, so the box takes up no room at all until
 * a tag that has subtags is picked.
 */
const SubtagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px 4px;

  &:empty {
    padding: 0;
  }
`;
/**
 * Wraps whatever is growing or shrinking. The overflow is what lets a height
 * tween off `auto` clip its content instead of squashing it.
 *
 * `motion` goes on the outside of the styled component, not the other way
 * round: `styled(motion.div)` swallows `initial`/`animate`/`exit` before they
 * ever reach framer-motion, which leaves the section snapping open with no
 * inline height on it at all.
 */
const RevealBox = styled.div`
  overflow: hidden;
`;
const Reveal = motion.create(RevealBox);
const SubtagsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${theme.colors.gray300};
`;
const SubtagsTitle = styled.span`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
  color: ${theme.colors.gray};
`;
/**
 * A collapsed section. The whole header is the hit area, which is the only
 * affordance the design gives for swapping which section is open.
 */
const SubtagsToggle = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: ${theme.colors.gray};
  cursor: pointer;

  svg {
    position: absolute;
    right: 0;
  }
`;
/**
 * Stacks the title over the picked-subtags line, kept clear of the chevron so a
 * long line is cut off rather than running under it.
 */
const SubtagsToggleLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  max-width: calc(100% - 24px);
`;
/**
 * The picked-subtags line grows in as the pills above it collapse away, so the
 * header does not jump while the section is still closing.
 */
const SummaryRevealBox = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
`;
const SummaryReveal = motion.create(SummaryRevealBox);
/**
 * One line whatever was picked — a collapsed section must not grow, or the
 * modal is back to outgrowing the viewport.
 */
const SubtagsSummary = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  text-transform: uppercase;
  color: ${theme.colors.primary};
`;
const SubtagsList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;
const SubtagPill = styled.button<{ $active: boolean }>`
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  text-transform: uppercase;
  user-select: none;
  background: ${(props): string =>
    props.$active ? theme.colors.primary : "transparent"};
  color: ${(props): string =>
    props.$active ? theme.colors.black : theme.colors.white};
  border: 1px solid
    ${(props): string =>
      props.$active ? theme.colors.primary : theme.colors.gray200};
  transition:
    background 0.2s ease-in-out,
    color 0.2s ease-in-out,
    border 0.2s ease-in-out;
`;

export default PlaylistSubtagsPicker;
